import { useTranslation } from '@kostbar/i18n'
import type { LeadWindowRule } from '@/api/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

interface Props {
  value: Array<LeadWindowRule>
  onChange: (next: Array<LeadWindowRule>) => void
}

export function LeadWindowsEditor({ value, onChange }: Props) {
  const { t: tRestaurants } = useTranslation('restaurants')

  const update = (idx: number, patch: Partial<LeadWindowRule>) => {
    const next = value.slice()
    next[idx] = { ...next[idx], ...patch }
    // ensure mutual exclusivity
    if (patch.hasOwnProperty('untilLocal')) next[idx].afterLocal = undefined
    if (patch.hasOwnProperty('afterLocal')) next[idx].untilLocal = undefined
    onChange(next)
  }

  const remove = (idx: number) => {
    const next = value.slice()
    next.splice(idx, 1)
    onChange(next)
  }

  const add = () => {
    onChange([...value, { untilLocal: '11:00', minAdvanceMinutes: 30 }])
  }

  return (
    <div className="space-y-3">
      {value.map((r, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="text-sm text-muted-foreground w-12">
            {tRestaurants('scheduling.lead.rule', { index: i + 1 })}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm">
              {tRestaurants('scheduling.lead.until')}
            </span>
            <Input
              type="time"
              value={r.untilLocal ?? ''}
              onChange={(e) =>
                update(i, { untilLocal: e.target.value || undefined })
              }
              className="w-28"
            />
          </div>
          <div className="text-sm text-muted-foreground">
            {tRestaurants('scheduling.lead.or')}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm">
              {tRestaurants('scheduling.lead.after')}
            </span>
            <Input
              type="time"
              value={r.afterLocal ?? ''}
              onChange={(e) =>
                update(i, { afterLocal: e.target.value || undefined })
              }
              className="w-28"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm">
              {tRestaurants('scheduling.lead.minAdvance')}
            </span>
            <Input
              type="number"
              value={r.minAdvanceMinutes}
              onChange={(e) =>
                update(i, {
                  minAdvanceMinutes: Math.max(0, parseInt(e.target.value) || 0),
                })
              }
              className="w-28"
            />
          </div>
          <Button variant="ghost" size="sm" onClick={() => remove(i)}>
            {tRestaurants('scheduling.lead.remove')}
          </Button>
        </div>
      ))}
      <div>
        <Button onClick={add} size="sm">
          {tRestaurants('scheduling.lead.addRule')}
        </Button>
      </div>
    </div>
  )
}
